import React, { useEffect, useRef, useCallback } from 'react';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { TListingClient } from 'types/TListing';
import { TAdmarktItemClient } from 'types/TAdmarkt';
import useAdmarktSimilarItems from '../../hooks/useAdmarktSimilarItems';
import AdmarktSimilarItemsList from './AdmarktSimilarItemsList';
import Classes from './AdmarktSimilarItems.scss';
import { trackGAEvent } from '../../../../utils/gaTracking';
import createCustomDimension from '../../../../utils/createCustomDimension';
import AdmarktSimilarItemsListSkeleton from './AdmarktSimilarItemsListSkeleton';
import { TFeedItem } from 'types/TFeeds';

type TSimilarItem = (TAdmarktItemClient | TFeedItem) & { itemId: string };

type TAdmarktSimilarItemsProps = {
  itemId: TListingClient['itemId'];
  sellerId: TListingClient['seller']['id'];
  category: TListingClient['category'];
  isSimilarItemsFeedGroupBEnabled?: boolean;
  isSimilarItemsFeedGroupCEnabled?: boolean;
};

const AdmarktSimilarItems = ({
  itemId,
  sellerId,
  category,
  isSimilarItemsFeedGroupBEnabled,
  isSimilarItemsFeedGroupCEnabled,
}: TAdmarktSimilarItemsProps) => {
  const { t } = useI18nContext();
  const { items, isLoading } = useAdmarktSimilarItems({
    itemId,
    sellerId,
    category,
    isSimilarItemsFeedGroupBEnabled,
    isSimilarItemsFeedGroupCEnabled,
  });
  const isExperimentFeedEnabled = isSimilarItemsFeedGroupBEnabled || isSimilarItemsFeedGroupCEnabled;
  const gaTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sendVisibleItemsGAEvent = useCallback((count: number) => {
    if (count > 0) {
      trackGAEvent('Display', 'RecommendedItemBlock', {
        ...createCustomDimension({
          dimension: 'onsite_search_results_page_size',
          payload: count,
        }),
      });
    }
  }, []);

  const debouncedSendGA = useCallback(
    (count: number) => {
      if (gaTimeoutRef.current) {
        clearTimeout(gaTimeoutRef.current);
      }
      gaTimeoutRef.current = setTimeout(() => {
        sendVisibleItemsGAEvent(count);
      }, 500);
    },
    [sendVisibleItemsGAEvent],
  );

  const handleVisibleItemsChange = useCallback(
    (visibleItemIds: Set<string>) => {
      debouncedSendGA(visibleItemIds.size);
    },
    [debouncedSendGA],
  );

  useEffect(() => {
    return () => {
      if (gaTimeoutRef.current) {
        clearTimeout(gaTimeoutRef.current);
      }
    };
  }, []);

  if (!items?.length && !isLoading) {
    return null;
  }

  return (
    <div className={`block-wrapper ${Classes.root}`}>
      <div className={Classes.headerTitle}>{t('similarItems.seeOther')}</div>
      {isLoading && <AdmarktSimilarItemsListSkeleton isExperimentFeedEnabled={isExperimentFeedEnabled} />}
      {!isLoading && items?.length && (
        <div className={Classes.similarItemsExperiment}>
          <AdmarktSimilarItemsList
            items={items as TSimilarItem[]}
            isExperimentFeedEnabled={isExperimentFeedEnabled}
            onVisibleItemsChange={handleVisibleItemsChange}
          />
        </div>
      )}
    </div>
  );
};

export default AdmarktSimilarItems;
